import { API_BASE, ATHENA_PLATFORM, IS_NATIVE_APP } from '../config/api';

const firebaseConfig = {
  apiKey: 'AIzaSyAie6QgatnfAGxxwdzKKzDM_BOs8o_q9VE',
  authDomain: 'athena-notificacoes.firebaseapp.com',
  projectId: 'athena-notificacoes',
  storageBucket: 'athena-notificacoes.firebasestorage.app',
  messagingSenderId: '268630738615',
  appId: '1:268630738615:web:5e62e5f5272399e9ceaf32',
};

const vapidKey = 'BFGBgoz1WqxircuvoZ3-c2Tj4LvHfFhcyDuvo1J6fBma2_UCs5Xm9gvHb5TTe1-6nnbWNhrSQ218BhV47tEdla8';
let foregroundUnsubscribe = null;
let firebaseSdkPromise = null;

/**
 * Firebase Web Messaging must never initialise inside Capacitor Android. A
 * dynamic import keeps the web SDK out of the native runtime path entirely;
 * browsers still receive the exact same Firebase PWA implementation.
 */
async function loadFirebaseMessagingSdk() {
  if (IS_NATIVE_APP) return null;
  if (!firebaseSdkPromise) {
    firebaseSdkPromise = Promise.all([
      import('firebase/app'),
      import('firebase/messaging'),
    ]).then(([appSdk, messagingSdk]) => ({
      initializeApp: appSdk.initializeApp,
      getApps: appSdk.getApps,
      getMessaging: messagingSdk.getMessaging,
      getToken: messagingSdk.getToken,
      isSupported: messagingSdk.isSupported,
      onMessage: messagingSdk.onMessage,
    })).catch((error) => {
      // A later browser retry can recover from a transient chunk/network error.
      firebaseSdkPromise = null;
      throw error;
    });
  }
  return firebaseSdkPromise;
}

export function athenaDeviceId() {
  const key = 'athena_push_device_id';
  let value = localStorage.getItem(key);
  if (!value) { value = crypto.randomUUID(); localStorage.setItem(key, value); }
  return value;
}

function platform() {
  if (IS_NATIVE_APP) return `${ATHENA_PLATFORM}-native`;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios-web';
  if (/Android/i.test(ua)) return 'android-web';
  return 'desktop-web';
}

async function messagingSetup() {
  // Firebase Web Messaging depende de service worker e da API Notification da
  // web. O shell Android não registra esse caminho: o push nativo entra numa
  // fase posterior, evitando uma permissão enganosa ou token inválido.
  if (IS_NATIVE_APP || !('serviceWorker' in navigator)) return null;
  const sdk = await loadFirebaseMessagingSdk();
  if (!sdk || !(await sdk.isSupported())) return null;
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  const app = sdk.getApps()[0] || sdk.initializeApp(firebaseConfig);
  return { messaging: sdk.getMessaging(app), registration, sdk };
}

export async function registerAthenaPush(token) {
  if (IS_NATIVE_APP) return { ok: false, reason: 'native_push_not_configured' };
  if (!token || typeof Notification === 'undefined' || Notification.permission !== 'granted') return { ok: false, reason: 'permission' };
  const setup = await messagingSetup();
  if (!setup) return { ok: false, reason: 'unsupported' };
  const { messaging, registration, sdk } = setup;
  const registrationToken = await sdk.getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
  if (!registrationToken) return { ok: false, reason: 'token' };

  const response = await fetch(`${API_BASE}/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ token: registrationToken, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza', device_id: athenaDeviceId(), platform: platform(), user_agent: navigator.userAgent || '' }),
  });
  if (!response.ok) throw new Error(`Não foi possível registrar notificações (${response.status})`);
  return { ok: true, device_id: athenaDeviceId(), platform: platform() };
}

// Mensagem com o app aberto: não criamos uma segunda notificação do sistema.
// A interface recebe o evento e sincroniza a central interna persistida.
export async function observeAthenaForeground(onPayload) {
  if (IS_NATIVE_APP) return null;
  if (foregroundUnsubscribe) return foregroundUnsubscribe;
  const setup = await messagingSetup();
  if (!setup) return null;
  foregroundUnsubscribe = setup.sdk.onMessage(setup.messaging, (payload) => onPayload?.(payload.data || {}));
  return foregroundUnsubscribe;
}

export async function getAthenaPushStatus(token) {
  const base = {
    supported: !IS_NATIVE_APP && 'Notification' in window && 'serviceWorker' in navigator,
    permission: IS_NATIVE_APP ? 'native-not-configured' : ('Notification' in window ? Notification.permission : 'unsupported'),
    device_id: athenaDeviceId(),
    platform: platform(),
    installed: IS_NATIVE_APP || window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true,
  };
  if (!token) return base;
  const response = await fetch(`${API_BASE}/push/status`, { headers: { Authorization: `Bearer ${token}` } });
  return response.ok ? { ...base, ...(await response.json()) } : base;
}

// Envia um push pelo mesmo caminho usado por lembretes. Assim o teste confirma
// Firebase + service worker, e não apenas a notificação local do navegador.
export async function testAthenaPush(token) {
  if (IS_NATIVE_APP) return { ok: false, reason: 'native_push_not_configured' };
  if (!token) throw new Error('Sessão não encontrada');
  const response = await fetch(`${API_BASE}/push/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    // A test must prove one browser installation; it is never a broadcast to
    // all subscriptions owned by the same account.
    body: JSON.stringify({ device_id: athenaDeviceId() }),
  });
  if (!response.ok) throw new Error(`Teste de push falhou (${response.status})`);
  return response.json();
}
